import { useState } from "react";
import { Link } from "react-router-dom";
import { AppLayout } from "@/components/AppLayout";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/contexts/AuthContext";
import { getTenantSlug } from "@/lib/api";
import {
  Users,
  CalendarDays,
  FileText,
  BarChart3,
  Briefcase,
  ClipboardList,
  DollarSign,
  Star,
  Workflow,
  MessageSquarePlus,
  UserPlus,
  Bell,
  Building2,
  ShieldAlert,
  Search,
  Sparkles,
  Mail,
  Globe,
  ChevronRight,
  Copy,
  Check,
} from "lucide-react";

// ─── Content definitions ───────────────────────────────────────────────────────

type Lang = "en" | "mk";

interface Example {
  prompt: string;
}

interface ModuleSection {
  icon: React.ElementType;
  color: string;
  title: { en: string; mk: string };
  description: { en: string; mk: string };
  examples: { en: string; mk: string }[];
}

const MODULES: ModuleSection[] = [
  {
    icon: Users,
    color: "bg-blue-50 text-blue-600 border-blue-100",
    title: { en: "Employees", mk: "Вработени" },
    description: {
      en: "Search, view, create, and update employee records.",
      mk: "Пребарај, прегледај, создај и ажурирај записи за вработени.",
    },
    examples: [
      { en: "List all active employees in the Engineering department", mk: "Прикажи ги сите активни вработени во Инженерство" },
      { en: "Get details for employee John Smith", mk: "Прикажи ги деталите за вработениот Јован Смит" },
      { en: "Create a new employee: Jane Doe, jane@company.com, HR Manager", mk: "Создај нов вработен: Јана Доу, jana@company.com, HR Менаџер" },
      { en: "Update Ana's job title to Senior Developer", mk: "Промени ја позицијата на Ана во Виш Програмер" },
      { en: "What is Maria's current salary?", mk: "Која е тековната плата на Марија?" },
    ],
  },
  {
    icon: CalendarDays,
    color: "bg-green-50 text-green-600 border-green-100",
    title: { en: "Leave & Time Off", mk: "Отсуство и одмор" },
    description: {
      en: "Manage leave requests, approvals, balances, and leave types.",
      mk: "Управувај со барања за отсуство, одобренија, салда и видови отсуство.",
    },
    examples: [
      { en: "Show all pending leave requests", mk: "Прикажи ги сите барања за отсуство на чекање" },
      { en: "Approve leave request #42", mk: "Одобри барање за отсуство #42" },
      { en: "Reject leave request #15 — team is short-staffed", mk: "Одбиј барање за отсуство #15 — тимот е малоброен" },
      { en: "What are Ana's leave balances?", mk: "Кои се салдата на отсуство на Ана?" },
      { en: "What leave types does the company offer?", mk: "Какви видови отсуство нуди компанијата?" },
      { en: "Submit annual leave for employee #7 from 2025-08-01 to 2025-08-10", mk: "Поднеси годишен одмор за вработен #7 од 01.08.2025 до 10.08.2025" },
    ],
  },
  {
    icon: Briefcase,
    color: "bg-purple-50 text-purple-600 border-purple-100",
    title: { en: "Recruitment", mk: "Регрутација" },
    description: {
      en: "Manage job positions, applications, and AI CV scoring.",
      mk: "Управувај со слободни работни места, пријави и AI оценување на CVs.",
    },
    examples: [
      { en: "Show me the recruitment pipeline overview", mk: "Прикажи го преглед на регрутацискиот тек" },
      { en: "List all applications for the Frontend Developer position", mk: "Прикажи ги сите пријави за позицијата Frontend Developer" },
      { en: "Analyze the CV for application #12", mk: "Анализирај го CV-то за пријава #12" },
      { en: "Analyze all unscored CVs for position #3", mk: "Анализирај ги сите неоценети CV-та за позиција #3" },
      { en: "Shortlist applicant Ana Petrova (application #8)", mk: "Постави ја Ана Петрова (пријава #8) на краток список" },
      { en: "Reject application #5 — not a good fit", mk: "Одбиј пријава #5 — не одговара за позицијата" },
      { en: "Create a new job position: Data Analyst, Analytics department", mk: "Создај нова работна позиција: Аналитичар на Податоци, оддел Аналитика" },
      { en: "Open job position #4 for applications", mk: "Отвори работна позиција #4 за пријавување" },
    ],
  },
  {
    icon: DollarSign,
    color: "bg-emerald-50 text-emerald-600 border-emerald-100",
    title: { en: "Payroll", mk: "Плати" },
    description: {
      en: "View payroll runs, create new runs, add adjustments, and approve.",
      mk: "Прегледај, создај и одобри пресметки на плати и додај прилагодувања.",
    },
    examples: [
      { en: "Show payroll summary for the last 6 months", mk: "Прикажи го преглед на платите за последните 6 месеци" },
      { en: "Get payroll details for March 2025", mk: "Прикажи ги деталите за платите за март 2025" },
      { en: "Create a payroll run for July 2025", mk: "Создај пресметка на плати за јули 2025" },
      { en: "Approve payroll run #8", mk: "Одобри пресметка на плати #8" },
      { en: "Add a €500 bonus for employee #3 in payroll run #10", mk: "Додај бонус од 500€ за вработен #3 во пресметка #10" },
      { en: "What is the total payroll cost for Q1 2025?", mk: "Кои се вкупните трошоци за плати во Q1 2025?" },
    ],
  },
  {
    icon: Star,
    color: "bg-amber-50 text-amber-600 border-amber-100",
    title: { en: "Performance", mk: "Перформанси" },
    description: {
      en: "Track KPIs, create performance reviews, and monitor goals.",
      mk: "Следи KPI-и, создај оцени на перформанси и следи цели.",
    },
    examples: [
      { en: "Show performance overview for the company", mk: "Прикажи го прегледот на перформансите на компанијата" },
      { en: "List KPIs for Ana Georgieva", mk: "Прикажи ги KPI-ите за Ана Георгиева" },
      { en: "Create a KPI for John: reach 95% customer satisfaction by December", mk: "Создај KPI за Јован: достигни 95% задоволство на клиенти до декември" },
      { en: "Write a performance review for Maria for Q2 2025, rating 4/5", mk: "Напиши оценка за перформанси за Марија за Q2 2025, оценка 4/5" },
    ],
  },
  {
    icon: ClipboardList,
    color: "bg-cyan-50 text-cyan-600 border-cyan-100",
    title: { en: "Onboarding & Offboarding", mk: "Вработување и напуштање" },
    description: {
      en: "Start onboarding for new hires, track tasks, and manage exit processes.",
      mk: "Започни процес на вработување, следи задачи и управувај со процеси на напуштање.",
    },
    examples: [
      { en: "Show all active onboarding sessions", mk: "Прикажи ги сите активни процеси на вработување" },
      { en: "Start onboarding for employee #5 using the 'Developer Onboarding' template", mk: "Започни процес на вработување за вработен #5 користејќи го шаблонот 'Developer Onboarding'" },
      { en: "Mark onboarding task #12 as completed", mk: "Означи ја задачата #12 за вработување како завршена" },
      { en: "Send a welcome message to the new hire in session #3", mk: "Испрати порака добредојде на новиот вработен во сесија #3" },
      { en: "Show active offboarding requests", mk: "Прикажи ги активните барања за напуштање" },
      { en: "Start offboarding for employee #9 — resignation, last day 2025-09-30", mk: "Започни процес на напуштање за вработен #9 — оставка, последен ден 30.09.2025" },
    ],
  },
  {
    icon: FileText,
    color: "bg-slate-50 text-slate-600 border-slate-100",
    title: { en: "Documents", mk: "Документи" },
    description: {
      en: "List documents and send them for employee signatures.",
      mk: "Прегледај документи и испрати ги на потпис до вработените.",
    },
    examples: [
      { en: "Show all documents waiting to be signed", mk: "Прикажи ги сите документи кои чекаат потпис" },
      { en: "List all documents for employee Ana Kovachevska", mk: "Прикажи ги сите документи за вработената Ана Ковачевска" },
      { en: "Send document #7 for signing", mk: "Испрати документ #7 на потпис" },
    ],
  },
  {
    icon: MessageSquarePlus,
    color: "bg-rose-50 text-rose-600 border-rose-100",
    title: { en: "Surveys", mk: "Анкети" },
    description: {
      en: "Create, activate, close surveys and view response statistics.",
      mk: "Создај, активирај и затвори анкети и прегледај статистики.",
    },
    examples: [
      { en: "List all active surveys", mk: "Прикажи ги сите активни анкети" },
      { en: "Activate survey #4", mk: "Активирај ја анкетата #4" },
      { en: "Close survey #2 — collection period is over", mk: "Затвори ја анкетата #2 — периодот за собирање е завршен" },
      { en: "Show results for survey #3", mk: "Прикажи ги резултатите за анкетата #3" },
      { en: "Create a pulse survey: 'Are you happy with the work environment?'", mk: "Создај анкета за пулс: 'Дали сте задоволни со работната средина?'" },
    ],
  },
  {
    icon: Workflow,
    color: "bg-violet-50 text-violet-600 border-violet-100",
    title: { en: "Workflows & Tasks", mk: "Работни текови и задачи" },
    description: {
      en: "View, activate, and complete workflow automations and pending tasks.",
      mk: "Прегледај, активирај и заврши автоматизации и задачи на чекање.",
    },
    examples: [
      { en: "List all configured workflows", mk: "Прикажи ги сите конфигурирани работни текови" },
      { en: "Activate the 'Leave Approval' workflow", mk: "Активирај го работниот тек 'Одобрување отсуство'" },
      { en: "Show all pending approval tasks", mk: "Прикажи ги сите задачи на чекање за одобрување" },
      { en: "Approve workflow task #22", mk: "Одобри ја задачата #22 во работниот тек" },
      { en: "Show all running workflow instances", mk: "Прикажи ги сите активни работни текови" },
    ],
  },
  {
    icon: Building2,
    color: "bg-orange-50 text-orange-600 border-orange-100",
    title: { en: "Teams & Organization", mk: "Тимови и организација" },
    description: {
      en: "Manage teams, members, join requests, and departments.",
      mk: "Управувај со тимови, членови, барања за придружување и одделенија.",
    },
    examples: [
      { en: "List all teams in the company", mk: "Прикажи ги сите тимови во компанијата" },
      { en: "Create a new team called 'Mobile Squad'", mk: "Создај нов тим наречен 'Mobile Squad'" },
      { en: "Add employee #6 to the 'Backend Team'", mk: "Додај вработен #6 во тимот 'Backend Team'" },
      { en: "Show pending team join requests", mk: "Прикажи ги барањата за придружување кон тимови" },
      { en: "Approve join request #3", mk: "Одобри барање за придружување #3" },
      { en: "Show headcount by department", mk: "Прикажи го бројот на вработени по оддели" },
    ],
  },
  {
    icon: CalendarDays,
    color: "bg-teal-50 text-teal-600 border-teal-100",
    title: { en: "Corporate Calendar", mk: "Корпоративен календар" },
    description: {
      en: "View, add, and delete company events and public holidays.",
      mk: "Прегледај, додај и бриши настани и државни празници.",
    },
    examples: [
      { en: "What events are coming up this month?", mk: "Кои настани претстојат овој месец?" },
      { en: "Add a public holiday: Independence Day, 2025-09-08", mk: "Додај државен празник: Ден на независноста, 08.09.2025" },
      { en: "Create a company event: Team Building, 2025-07-15, Sofia Office", mk: "Создај настан: Team Building, 15.07.2025, Канцеларија Софија" },
      { en: "Delete corporate event #5", mk: "Избриши корпоративен настан #5" },
    ],
  },
  {
    icon: ShieldAlert,
    color: "bg-red-50 text-red-600 border-red-100",
    title: { en: "Raise a Concern", mk: "Пријави проблем" },
    description: {
      en: "View and respond to anonymous concern cases raised by employees.",
      mk: "Прегледај и одговори на анонимни пријави на проблеми од вработените.",
    },
    examples: [
      { en: "Show all open concern cases", mk: "Прикажи ги сите отворени пријави на проблеми" },
      { en: "Show concern cases under review", mk: "Прикажи ги пријавите на проблеми во разгледување" },
      { en: "Update concern case #2 to 'resolved' and send a reply", mk: "Ажурирај ја пријавата #2 на 'решена' и испрати одговор" },
    ],
  },
  {
    icon: Bell,
    color: "bg-yellow-50 text-yellow-600 border-yellow-100",
    title: { en: "Notifications", mk: "Известувања" },
    description: {
      en: "View and manage your notifications, or send announcements to staff.",
      mk: "Прегледај и управувај со известувањата или испрати соопштенија до вработените.",
    },
    examples: [
      { en: "Show my unread notifications", mk: "Прикажи ги моите непрочитани известувања" },
      { en: "Mark all my notifications as read", mk: "Означи ги сите мои известувања како прочитани" },
      { en: "Send an announcement to all employees: 'Office closed on Friday'", mk: "Испрати соопштение до сите вработени: 'Канцеларијата е затворена в петок'" },
    ],
  },
  {
    icon: BarChart3,
    color: "bg-indigo-50 text-indigo-600 border-indigo-100",
    title: { en: "HR Analytics", mk: "HR Аналитика" },
    description: {
      en: "Get insights on headcount, turnover, leave usage, payroll cost, and department comparisons.",
      mk: "Добиј увид во бројот на вработени, флуктуација, користење на отсуство, трошоци за плати и споредба на одделенија.",
    },
    examples: [
      { en: "What is our current headcount?", mk: "Колкав е тековниот број на вработени?" },
      { en: "Show turnover trend for the past year", mk: "Прикажи го трендот на флуктуација за изминатата година" },
      { en: "Compare all departments by headcount and average tenure", mk: "Спореди ги сите одделенија по број на вработени и просечен стаж" },
      { en: "Show leave usage by department this year", mk: "Прикажи го користењето на отсуство по одделенија оваа година" },
      { en: "Who has upcoming birthdays or work anniversaries?", mk: "Кој има претстоечки роденден или работна годишнина?" },
    ],
  },
];

// ─── "Unknown" contact card ───────────────────────────────────────────────────

const UNKNOWN_TEXT = {
  en: {
    heading: "Can't find what you need?",
    body: "If Sara doesn't know how to handle your request, we'd love to hear about it so we can add that functionality for you.",
    label: "Describe what you're trying to do",
    placeholder: "e.g. I want Sara to be able to generate a custom PDF report...",
    cta: "Send request to BestHR",
    copied: "Copied!",
    copy: "Copy email",
    subject: "Sara feature request",
  },
  mk: {
    heading: "Не можеш да го најдеш тоа што ти треба?",
    body: "Ако Сара не знае како да го обработи твоето барање, споделете не со нас за да можеме да ја додадеме таа функционалност.",
    label: "Опиши го она што сакаш да го направиш",
    placeholder: "на пр. Сакам Сара да може да генерира прилагоден PDF извештај...",
    cta: "Испрати барање до BestHR",
    copied: "Копирано!",
    copy: "Копирај е-пошта",
    subject: "Барање за функција на Сара",
  },
};

const PAGE_TEXT = {
  en: {
    title: "Sara Help Centre",
    subtitle: "Sara is your AI-powered HR assistant. She can help you manage almost any action on the platform — just type what you need in plain language.",
    tip: "Tip: Go to the",
    tipLink: "Sara assistant page",
    tipEnd: "and type any of the example prompts below to get started.",
    searchPlaceholder: "Search modules or example prompts…",
    examplesLabel: "Example prompts",
    langToggle: "Македонски",
  },
  mk: {
    title: "Центар за помош на Сара",
    subtitle: "Сара е вашиот AI асистент за управување со HR. Таа може да ви помогне да извршите речиси секоја акција на платформата — само напишете што ви треба на обичен јазик.",
    tip: "Совет: Одете на",
    tipLink: "страницата на Сара",
    tipEnd: "и напишете кој било пример подолу за да започнете.",
    searchPlaceholder: "Пребарај модули или примери…",
    examplesLabel: "Примери",
    langToggle: "English",
  },
};

// ─── Component ────────────────────────────────────────────────────────────────

export default function SaraHelp() {
  const { user } = useAuth();
  const tenant = getTenantSlug();
  const [lang, setLang] = useState<Lang>("en");
  const [search, setSearch] = useState("");
  const [featureRequest, setFeatureRequest] = useState("");
  const [copied, setCopied] = useState(false);

  const t = PAGE_TEXT[lang];
  const ut = UNKNOWN_TEXT[lang];

  // Filter modules by search
  const filtered = MODULES.filter((m) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      m.title[lang].toLowerCase().includes(q) ||
      m.description[lang].toLowerCase().includes(q) ||
      m.examples.some((e) => e[lang].toLowerCase().includes(q))
    );
  });

  // Build mailto link
  const buildMailto = () => {
    const body = [
      featureRequest ? `Request:\n${featureRequest}` : "",
      `\n---`,
      `User: ${user?.name ?? "Unknown"} (${user?.email ?? ""})`,
      `Tenant: ${tenant}`,
      `Platform: ${window.location.origin}`,
    ].join("\n");
    return `mailto:hey@besthr.app?subject=${encodeURIComponent(ut.subject)}&body=${encodeURIComponent(body)}`;
  };

  const handleCopyEmail = () => {
    navigator.clipboard.writeText("hey@besthr.app").then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <AppLayout>
      <div className="space-y-6 w-full">

        {/* ── Header ── */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="space-y-2">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 shadow-sm">
                <Sparkles className="h-5 w-5 text-white" />
              </div>
              <h1 className="text-2xl font-bold tracking-tight">{t.title}</h1>
            </div>
            <p className="text-muted-foreground max-w-2xl leading-relaxed">
              {t.subtitle}
            </p>
            <p className="text-sm text-muted-foreground">
              {t.tip}{" "}
              <Link to="/ai-assistant" className="text-violet-600 hover:underline font-medium inline-flex items-center gap-1">
                {t.tipLink} <ChevronRight className="h-3 w-3" />
              </Link>{" "}
              {t.tipEnd}
            </p>
          </div>

          {/* Language toggle */}
          <Button
            variant="outline"
            size="sm"
            className="flex items-center gap-2 shrink-0"
            onClick={() => setLang((l) => (l === "en" ? "mk" : "en"))}
          >
            <Globe className="h-4 w-4" />
            {t.langToggle}
          </Button>
        </div>

        {/* ── Search ── */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t.searchPlaceholder}
            className="w-full rounded-lg border bg-background pl-10 pr-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-violet-500/30 focus:border-violet-400 transition"
          />
        </div>

        {/* ── Module cards ── */}
        <div className="grid gap-5 md:grid-cols-2">
          {filtered.map((mod) => {
            const Icon = mod.icon;
            return (
              <div
                key={mod.title.en}
                className="rounded-xl border bg-card shadow-sm hover:shadow-md transition-shadow p-5 flex flex-col gap-3"
              >
                {/* Card header */}
                <div className="flex items-center gap-3">
                  <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border ${mod.color}`}>
                    <Icon className="h-4 w-4" />
                  </div>
                  <div>
                    <h2 className="font-semibold text-sm leading-tight">{mod.title[lang]}</h2>
                    <p className="text-xs text-muted-foreground mt-0.5">{mod.description[lang]}</p>
                  </div>
                </div>

                {/* Example prompts */}
                <div className="space-y-1.5">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    {t.examplesLabel}
                  </p>
                  <ul className="space-y-1">
                    {mod.examples.map((ex, i) => (
                      <li key={i} className="flex items-start gap-2 group">
                        <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-violet-400" />
                        <Link
                          to={`/ai-assistant?prompt=${encodeURIComponent(ex[lang])}`}
                          className="text-sm text-foreground/80 hover:text-violet-700 hover:underline transition-colors leading-snug"
                        >
                          {ex[lang]}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            );
          })}
        </div>

        {filtered.length === 0 && (
          <div className="text-center py-16 text-muted-foreground">
            <Search className="mx-auto h-8 w-8 mb-3 opacity-40" />
            <p className="font-medium">No results found for "{search}"</p>
            <p className="text-sm mt-1">Try a different keyword or scroll through all modules above.</p>
          </div>
        )}

        {/* ── Feature request card ── */}
        <div className="rounded-xl border bg-gradient-to-br from-violet-50 to-purple-50 dark:from-violet-950/30 dark:to-purple-950/30 border-violet-200 dark:border-violet-800 p-6 space-y-4">
          <div className="flex items-start gap-3">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-violet-100 dark:bg-violet-900 text-violet-600 dark:text-violet-300">
              <Mail className="h-4 w-4" />
            </div>
            <div>
              <h2 className="font-semibold">{ut.heading}</h2>
              <p className="text-sm text-muted-foreground mt-1">{ut.body}</p>
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">{ut.label}</label>
            <Textarea
              value={featureRequest}
              onChange={(e) => setFeatureRequest(e.target.value)}
              placeholder={ut.placeholder}
              className="min-h-[90px] bg-white dark:bg-background resize-none"
            />
          </div>

          {/* Pre-filled user context info */}
          <div className="rounded-lg bg-white/70 dark:bg-black/20 border border-violet-100 dark:border-violet-800 px-4 py-3 text-xs text-muted-foreground space-y-0.5">
            <p><span className="font-medium text-foreground">User:</span> {user?.name} ({user?.email})</p>
            <p><span className="font-medium text-foreground">Tenant:</span> {tenant}</p>
            <p className="text-xs opacity-70">This info will be included automatically in your email so we can identify your account.</p>
          </div>

          <div className="flex flex-wrap gap-3">
            <Button asChild className="bg-violet-600 hover:bg-violet-700 text-white gap-2">
              <a href={buildMailto()}>
                <Mail className="h-4 w-4" />
                {ut.cta}
              </a>
            </Button>
            <Button variant="outline" size="sm" className="gap-2" onClick={handleCopyEmail}>
              {copied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
              {copied ? ut.copied : ut.copy}
            </Button>
          </div>
        </div>

      </div>
    </AppLayout>
  );
}
